import type { Asset } from "@cva/shared";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { client, orpc } from "../lib/api";
import { requestCutout, waitForCutout } from "../lib/cutout";
import { errorMessage } from "../lib/utils";
import type { EditorStore } from "./store";

/**
 * Background removal in the editors: an image element, a still on the main
 * track or on a track above. While the server works, the target keeps its
 * image; when the cut-out copy is ready it replaces the image in one undoable
 * step – also when the selection moved on meanwhile.
 */
export type CutoutTarget = { kind: "element" | "clip" | "overlay"; id: string };

export interface PendingCutout {
  target: CutoutTarget;
  sourceId: string;
  /** The copy, once the server created it (it is being filled). */
  copy: Asset | null;
}

const keyOf = (target: CutoutTarget) => `${target.kind}:${target.id}`;

const usePending = create<{ pending: Record<string, PendingCutout> }>(() => ({ pending: {} }));
let controller = new AbortController();

function setPending(target: CutoutTarget, entry: PendingCutout | null) {
  usePending.setState((state) => {
    const pending = { ...state.pending };
    if (entry) pending[keyOf(target)] = entry;
    else delete pending[keyOf(target)];
    return { pending };
  });
}

/** The running background removal of a target, if any. */
export function usePendingCutout(target: CutoutTarget): PendingCutout | undefined {
  return usePending((state) => state.pending[keyOf(target)]);
}

/** The asset a target currently shows. */
function assetOf(store: EditorStore, target: CutoutTarget): string | null {
  const { doc } = store.getState();
  if (target.kind === "element") {
    const element = doc.elements[target.id];
    return element?.type === "image" ? element.assetId : null;
  }
  const clips = target.kind === "clip" ? doc.timeline?.clips : doc.timeline?.overlays;
  return clips?.find((clip) => clip.id === target.id)?.assetId ?? null;
}

type Named = Pick<Asset, "id" | "name">;

/**
 * Swaps the target's image when it still shows `from`. The copy has the same
 * size and orientation, so crop, framing and position stay as they are. A
 * name taken over from the image (layer, clip) follows it.
 */
function swapAsset(store: EditorStore, target: CutoutTarget, from: Named, to: Named): boolean {
  if (assetOf(store, target) !== from.id || store.getState().readOnly) return false;
  store.getState().update((draft) => {
    const item =
      target.kind === "element"
        ? draft.elements[target.id]
        : (target.kind === "clip" ? draft.timeline?.clips : draft.timeline?.overlays)?.find(
            (clip) => clip.id === target.id,
          );
    if (!item || !("assetId" in item) || item.assetId !== from.id) return;
    if (target.kind === "element" && (item as { type?: string }).type !== "image") return;
    item.assetId = to.id;
    if (item.name === from.name) item.name = to.name;
  });
  return assetOf(store, target) === to.id;
}

/** Removes the background of the target's image; the image is replaced when done. */
export async function startCutout(options: {
  orgId: string;
  store: EditorStore;
  queryClient: QueryClient;
  target: CutoutTarget;
  source: Named;
}): Promise<void> {
  const { orgId, store, queryClient, target, source } = options;
  const sourceId = source.id;
  if (usePending.getState().pending[keyOf(target)]) return;
  const signal = controller.signal;
  setPending(target, { target, sourceId, copy: null });
  try {
    const copy = await requestCutout(queryClient, orgId, sourceId);
    setPending(target, { target, sourceId, copy });
    const result = await waitForCutout(queryClient, orgId, copy, {
      signal,
      onUpdate: (asset) => {
        if (!signal.aborted) setPending(target, { target, sourceId, copy: asset });
      },
    });
    if (signal.aborted) return;
    if (result.status === "failed") {
      toast.error(result.error ?? "Das Freistellen ist fehlgeschlagen.");
      return;
    }
    if (result.status !== "ready") return;
    if (!swapAsset(store, target, source, result)) {
      // Replaced or deleted meanwhile: the copy is still in the media library.
      toast.success(`„${result.name}“ liegt jetzt in der Mediathek.`);
    }
  } catch (error) {
    if (!signal.aborted) toast.error(errorMessage(error));
  } finally {
    if (!signal.aborted) setPending(target, null);
  }
}

/** Puts the original image back (it must still exist outside the trash). */
export async function restoreOriginal(options: {
  orgId: string;
  store: EditorStore;
  queryClient: QueryClient;
  target: CutoutTarget;
  copy: Named & { cutout?: Asset["cutout"] };
}): Promise<void> {
  const { orgId, store, target, copy } = options;
  const sourceId = copy.cutout?.sourceId;
  if (!sourceId) return;
  const source = await client.media.get({ orgId, assetId: sourceId }).catch(() => null);
  if (source?.status !== "ready") {
    toast.error("Das Original liegt im Papierkorb oder wurde gelöscht.");
    return;
  }
  // The editor draws it right away instead of loading it first.
  options.queryClient.setQueryData(
    orpc.media.get.queryKey({ input: { orgId, assetId: sourceId } }),
    source,
  );
  swapAsset(store, target, copy, source);
}

/**
 * Ties running background removals to the open editor: leaving it stops
 * waiting (the copies still finish on the server and land in the media library).
 */
export function useCutoutLifecycle(): void {
  useEffect(
    () => () => {
      controller.abort();
      controller = new AbortController();
      usePending.setState({ pending: {} });
    },
    [],
  );
}
